
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useUserSearch } from "./useUserSearch";
import { 
  Profile,
  fetchUserProfiles,
  updateUserRole,
  updateUserVerification,
  deleteUser,
  checkAdminStatus,
  getFirstAdmin
} from "@/services/userService";

export function useUserManagement() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<Profile[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState<string | null>(null);
  const [isUpdatingVerification, setIsUpdatingVerification] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState(false);

  const { 
    searchQuery, 
    setSearchQuery, 
    filteredAdmins, 
    filteredRegularUsers 
  } = useUserSearch(users);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      setCurrentUserId(user.id);

      const isAdmin = await checkAdminStatus(user.id);
      if (!isAdmin) {
        toast.error("You don't have permission to access this page");
        navigate('/');
        return;
      }

      const firstAdmin = await getFirstAdmin();
      setIsOwner(firstAdmin?.id === user.id);
      setIsLoading(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.message);
      navigate('/');
    }
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const data = await fetchUserProfiles();
      setUsers(data);
    } catch (error: any) {
      toast.error("Failed to fetch users");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleRoleUpdate = async (userId: string, makeAdmin: boolean) => {
    if (userId === currentUserId) {
      toast.error("You cannot change your own role");
      return;
    }

    if (!makeAdmin && isOwner && userId === currentUserId) {
      toast.error("The owner's role cannot be changed");
      return;
    }

    setIsUpdatingRole(userId);
    try {
      // First update the database
      await updateUserRole(userId, makeAdmin);
      
      // After successful update, update the local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, is_admin: makeAdmin }
            : user
        )
      );
      
      // Refresh the user list to ensure we have the latest data
      await fetchUsers();
      
      toast.success(`User ${makeAdmin ? 'promoted' : 'demoted'} successfully`);
    } catch (error: any) {
      console.error("Failed to update user role:", error);
      toast.error("Failed to update user role: " + error.message);
    } finally {
      setIsUpdatingRole(null);
    }
  };

  const handleVerificationUpdate = async (userId: string, status: 'none' | 'verified' | 'verified_creative') => {
    setIsUpdatingVerification(userId);
    try {
      // First update the database
      await updateUserVerification(userId, status);
      
      // After successful update, update the local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { 
                ...user, 
                verification_status: status === 'verified_creative' ? 'verified' : status,
                is_creative: status === 'verified_creative'
              }
            : user
        )
      );
      
      // Refresh user list to ensure consistency with database
      await fetchUsers();
      
      toast.success(`User ${status === 'none' ? 'verification removed' : 
        status === 'verified_creative' ? 'verified as creative' : 'verified as traveler'} successfully`);
    } catch (error: any) {
      console.error("Failed to update verification status:", error);
      toast.error("Failed to update user verification status: " + error.message);
    } finally {
      setIsUpdatingVerification(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (userId === currentUserId) {
      toast.error("You cannot delete your own account from here. Use the settings page instead.");
      return;
    }

    if (userId === currentUserId && isOwner) {
      toast.error("The owner's account cannot be deleted");
      return;
    }

    if (!confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }

    setIsDeletingUser(userId);
    try {
      await deleteUser(userId);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      toast.success("User deleted successfully");
      
      // Refresh user list after deletion
      await fetchUsers();
    } catch (error: any) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user: " + error.message);
    } finally {
      setIsDeletingUser(null);
    }
  };

  return {
    isLoading,
    isLoadingUsers,
    searchQuery,
    setSearchQuery,
    currentUserId,
    isOwner,
    isUpdatingRole,
    isDeletingUser,
    isUpdatingVerification,
    filteredAdmins,
    filteredRegularUsers,
    handleRoleUpdate,
    handleDeleteUser,
    handleVerificationUpdate,
  };
}
